import React from 'react';

/* ------------------------------------------------------------------ */
/*  Pillar Type System                                                 */
/* ------------------------------------------------------------------ */

export type PillarType =
  | 'problems'
  | 'solutions'
  | 'enablers'
  | 'communities'
  | 'projects';

/* ------------------------------------------------------------------ */
/*  Pillar Data — the universal shape every pillar page consumes       */
/* ------------------------------------------------------------------ */

export interface PillarRelationships {
  problems_count: number;
  solutions_count: number;
  enablers_count: number;
  communities_count: number;
  projects_count: number;
}

export interface PillarData {
  guid: string;
  name: string;
  description?: string;
  category?: string;
  status?: string;
  /**
   * DEV-1409. Stable, i18n-independent status key used purely to COLOR the
   * tray Status value (never to display). When set, the header tints the
   * Status text via statusColor(statusKey); when undefined (all project
   * consumers today) the Status value keeps its default color. Chronicles
   * set this to a reach-state key (draft / followers / inReview /
   * reachingSearch / inCommunityFeed / paused) so the displayed reach
   * label is colored without color-matching on the translated string.
   */
  statusKey?: string;
  contentStatus?: string;
  /** DEV-1112. Lifecycle state — draft / active / complete / expired.
   *  Currently only set by Mini-Project consumers; Full Project leaves it
   *  undefined and the header omits the chip. Drives the KPI dot
   *  short-circuits in `computeKpiStatus.ts` (lifecycle='complete' → all
   *  blue, 'draft'/'expired' → all grey, 'active' → compute normally).
   *  Surfacing this in the header eliminates the "why is everything
   *  blue/grey on a project still in Planned" debugging trail. */
  lifecycleStatus?: string;

  /* Dates */
  startDate?: string;
  endDate?: string;

  /* Visual assets */
  heroImageUrl?: string;
  logoUrl?: string;
  countryFlagCode?: string;
  sdgBadges?: string[];

  /* Social stats */
  followersCount?: number;
  contributorsCount?: number;

  /* Community hierarchy */
  communityL1?: string;
  communityL2?: string;
  communityL3?: string;
  /** Entity GUID of the project's primary location (for community path links). */
  primaryLocationGuid?: string;

  /* Owner / tray display */
  ownerAvatarUrl?: string;
  /**
   * DEV-991. Owner kind, surfaced for the hero avatar click behaviour:
   * 'user' navigates to /profile/{guid} (Impact tab), 'community' navigates
   * to /communities/{guid}#alignment. Resolved via fetchOwnerDisplay.
   */
  ownerKind?: 'user' | 'community';
  /** DEV-991. GUID of the owning user or community. Same source as ownerKind. */
  ownerGuid?: string;
  /** DEV-991. Display name of the owner; used for the tooltip on the avatar. */
  ownerDisplayName?: string;
  /** Most-local community name (e.g. "Boston") for the hero badge. */
  communityDisplayName?: string;

  /* Cross-pillar relationship counts */
  relationships: PillarRelationships;
}

/* ------------------------------------------------------------------ */
/*  Tab Definition — consumed by ConfigurableTabSystem                 */
/* ------------------------------------------------------------------ */

export interface TabDefinition {
  key: string;
  label: string;
  icon?: React.ReactNode;
  badge?: number | string;
  disabled?: boolean;
  content: React.ReactNode;
}

/* ------------------------------------------------------------------ */
/*  Pillar Colours & Metadata                                          */
/* ------------------------------------------------------------------ */

/**
 * Hex values for canvas / SVG / programmatic contexts that can't use
 * CSS custom properties.  For CSS-based styling, prefer PILLAR_CSS_VARS.
 */
export const PILLAR_COLORS: Record<PillarType, string> = {
  problems: '#EF4444',   // red
  solutions: '#22c55e',  // green
  enablers: '#3B82F6',   // blue
  communities: '#E2E8F0', // white / light slate
  projects: '#A855F7',   // purple (accent — logo used in buttons)
};

/**
 * Accent colours used for the left-border highlight on pillar detail pages.
 * These match the user-facing brand palette per pillar:
 *   Problems = red, Solutions = green, Enablers = blue,
 *   Communities = white, Projects = dark grey.
 */
export const PILLAR_ACCENT_COLORS: Record<PillarType, string> = {
  problems:    '#EF4444',   // red
  solutions:   '#22c55e',   // green
  enablers:    '#3B82F6',   // blue
  communities: '#ffffff',   // white
  projects:    '#6B7280',   // dark grey
};

/**
 * CSS custom property references — use these in inline styles
 * or any CSS context so colours respond to theme changes.
 */
export const PILLAR_CSS_VARS: Record<PillarType, string> = {
  problems:    'var(--pillar-problems)',
  solutions:   'var(--pillar-solutions)',
  enablers:    'var(--pillar-enablers)',
  communities: 'var(--pillar-communities)',
  projects:    'var(--pillar-projects)',
};

export interface PillarMeta {
  key: PillarType;
  label: string;
  plural: string;
  color: string;
}

export const PILLAR_META: Record<PillarType, PillarMeta> = {
  problems: { key: 'problems', label: 'Problem', plural: 'Problems', color: '#EF4444' },
  solutions: { key: 'solutions', label: 'Solution', plural: 'Solutions', color: '#22c55e' },
  enablers: { key: 'enablers', label: 'Enabler', plural: 'Enablers', color: '#3B82F6' },
  communities: { key: 'communities', label: 'Community', plural: 'Communities', color: '#E2E8F0' },
  projects: { key: 'projects', label: 'Project', plural: 'Projects', color: '#A855F7' },
};

/** Ordered list for rendering pillar buttons in consistent sequence. */
export const PILLAR_ORDER: PillarType[] = [
  'problems',
  'solutions',
  'enablers',
  'communities',
  'projects',
];

/* ------------------------------------------------------------------ */
/*  Project → PillarData adapter                                       */
/* ------------------------------------------------------------------ */

/**
 * Convert a raw project record (from Data/projects.ts) into
 * the universal PillarData shape consumed by pillar page components.
 */
export function projectToPillarData(project: any): PillarData {
  return {
    guid: project.project_GUID ?? project.project_guid ?? '',
    name: project.project_name ?? 'Untitled Project',
    description: project.description ?? '',
    category: project.project_category ?? '',
    status: project.project_status ?? '',
    contentStatus: project.content_status ?? '',
    startDate: project.start_date ?? '',
    endDate: project.finish_date ?? '',
    heroImageUrl: project.hero_image_url ?? undefined,
    logoUrl: project.logo_url ?? project.tile_image_url ?? undefined,
    countryFlagCode: project.country_flag_code ?? undefined,
    sdgBadges: project.sdg_badges ?? [],
    followersCount: project.followers_count ?? 0,
    contributorsCount: project.contributors_count ?? 0,
    communityL1: project.community_l1 ?? '',
    communityL2: project.community_l2 ?? '',
    communityL3: project.community_l3 ?? '',
    primaryLocationGuid: project.primary_location_entity_guid ?? undefined,
    ownerAvatarUrl: project.owner_avatar_url ?? undefined,
    communityDisplayName:
      project.Community ?? project.community_l3 ?? project.community_l2 ?? '',
    relationships: {
      problems_count: project.problems_count ?? 3,
      solutions_count: project.solutions_count ?? 2,
      enablers_count: project.enablers_count ?? 5,
      communities_count: project.communities_count ?? 1,
      projects_count: project.related_projects_count ?? 4,
    },
  };
}
